import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { Link, useNavigate } from 'react-router-dom';
import { PUBLICATIONS_QUERY } from '../graphql/queries';
import { DELETE_PUBLICATION } from '../graphql/mutations';
import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import M3FAB from '../components/M3FAB';
import M3LinearProgress from '../components/M3Progress';
import '../styles/publications.css';

export default function PublicationsPage() {
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const { data, loading, error, refetch } = useQuery(PUBLICATIONS_QUERY);
  const [deletePublication] = useMutation(DELETE_PUBLICATION);
  const navigate = useNavigate();

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data client-side
  const sortedPublications = React.useMemo(() => {
    if (!data?.publications) return [];
    const sorted = [...data.publications];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === bVal) return 0;

        let comparison;
        if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else {
          comparison = aVal > bVal ? 1 : -1;
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return sorted;
  }, [data, sortConfig]);

  const handleDelete = async (publication) => {
    if (window.confirm(`Delete "${publication.title}"? This cannot be undone.`)) {
      try {
        await deletePublication({ variables: { input: { id: publication.id } } });
        refetch();
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  const handleRowClick = (publication) => {
    navigate(`/publications/${publication.id}`);
  };

  const renderActions = (publication) => (
    <div className="md3-table-actions">
      <span className="md3-action-divider">·</span>
      {publication.pages?.length > 0 && (
        <>
          <Link to={`/pages/${publication.pages[0].id}`} className="md3-action-link">
            Pages
          </Link>
          <span className="md3-action-divider">·</span>
        </>
      )}
      <Link to={`/publications/${publication.id}/edit`} className="md3-action-link">
        Edit
      </Link>
      <span className="md3-action-divider">·</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(publication);
        }}
        className="md3-action-link md3-action-link-danger"
        title="Delete publication"
      >
        Delete
      </button>
    </div>
  );

  const columns = [
    { key: 'title', title: 'Title', render: (v) => <strong>{v}</strong> },
    { key: 'author', title: 'Author' },
    { key: 'year', title: 'Year' },
    { key: 'public', title: 'Public', render: (v) => (v ? 'Yes' : 'No') },
    { key: 'figuresCount', title: 'Figures', render: (v) => v || 0 }
  ];

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );

  if (!sortedPublications.length && !loading) {
    return (
      <div className="page-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="md3-title-large">Publications</h1>
          <Link to="/publications/new" className="btn btn-primary">
            <span className="material-symbols-rounded">add</span>
            Upload Publication
          </Link>
        </div>
        <EmptyState
          icon="menu_book"
          title="No Publications"
          message="No publications have been uploaded yet. Upload your first PDF to get started."
          action={
            <Link to="/publications/new" className="btn btn-primary mt-3">
              Upload Publication
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="md3-title-large">Publications</h1>
        <Link to="/publications/new" className="btn btn-primary">
          <span className="material-symbols-rounded">add</span>
          Upload Publication
        </Link>
      </div>

      {loading && <M3LinearProgress indeterminate />}

      <DataTable
        columns={columns}
        data={sortedPublications}
        loading={loading}
        rowKey="id"
        onRowClick={handleRowClick}
        sortConfig={sortConfig}
        onSort={handleSort}
        actions={renderActions}
        emptyMessage="No publications found"
      />

      {/* FAB for quick upload — visible on scroll */}
      <M3FAB
        variant="primary"
        icon={<span className="material-symbols-rounded">upload_file</span>}
        label="Upload"
        onClick={() => navigate('/publications/new')}
        className="publications-fab"
      />
    </div>
  );
}
